import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { SecondaryButton } from "./AdminUI";
import { Check, X } from "lucide-react";

function diffField(oldText, newText) {
  return (oldText || "").trim() !== (newText || "").trim();
}

/**
 * Props:
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 *  - title: string  (e.g. "Refine sustainability claims with AI")
 *  - refined: [{ index, current: {title,url,description}, suggested: {title,url,description} } | null]
 *  - newEntries: [{ suggested: {title,url,description} }]
 *  - onApplyEdit: (index, suggested) => void     // replace the existing entry at `index` with `suggested`
 *  - onAddNew: (suggested) => void               // append the suggested entry to the list
 */
export function RefineEntriesModal({ open, onOpenChange, title, refined, newEntries, onApplyEdit, onAddNew }) {
  // Track which suggestion rows have been resolved within this open session
  const [resolved, setResolved] = useState(new Set());

  const refinedRows = (refined || []).filter(Boolean).map((r) => ({ kind: "edit", key: `edit-${r.index}`, row: r }));
  const newRows = (newEntries || []).map((n, i) => ({ kind: "new", key: `new-${i}`, row: n }));
  const all = [...refinedRows, ...newRows];
  const visible = all.filter((r) => !resolved.has(r.key));

  function resolve(key) {
    const next = new Set(resolved);
    next.add(key);
    setResolved(next);
    if (all.every((r) => next.has(r.key))) {
      setResolved(new Set());
      onOpenChange(false);
    }
  }

  function approve(item) {
    if (item.kind === "edit") onApplyEdit(item.row.index, item.row.suggested);
    else onAddNew(item.row.suggested);
    resolve(item.key);
  }

  function discard(item) {
    resolve(item.key);
  }

  function close() {
    setResolved(new Set());
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="refine-entries-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Each suggestion is grounded in fetched source content. Click <strong>Approve</strong> to apply it to the form, or
            <strong> Discard</strong> to skip. You&apos;ll still need to click <strong>Save</strong> on the form to persist changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {visible.map((item) => {
            if (item.kind === "edit") {
              const { current, suggested } = item.row;
              const titleChanged = diffField(current.title, suggested.title);
              const descChanged = diffField(current.description, suggested.description);
              return (
                <div key={item.key} className="border border-[#E9ECEF] rounded-md p-3 bg-white" data-testid={`refine-edit-${item.row.index}`}>
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <div className="text-xs uppercase tracking-wider text-[#6C757D]">Edit existing entry #{item.row.index + 1}</div>
                    <ApproveDiscard onApprove={() => approve(item)} onDiscard={() => discard(item)} testidPrefix={`refine-edit-${item.row.index}`} />
                  </div>
                  <FieldRow label="Title" current={current.title} suggested={suggested.title} changed={titleChanged} />
                  <FieldRow label="Description" current={current.description} suggested={suggested.description} changed={descChanged} />
                  <div className="text-[11px] text-[#6C757D] mt-1">Source URL preserved: <span className="font-mono">{current.url || "—"}</span></div>
                </div>
              );
            }
            const { suggested } = item.row;
            return (
              <div key={item.key} className="border border-leaf/40 rounded-md p-3 bg-leaf/5" data-testid={item.key}>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <div className="text-xs uppercase tracking-wider text-leaf font-semibold">New entry suggestion</div>
                  <ApproveDiscard onApprove={() => approve(item)} onDiscard={() => discard(item)} testidPrefix={item.key} />
                </div>
                <div className="space-y-1">
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6C757D] mr-2">Title</span><span className="text-sm font-medium">{suggested.title}</span></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6C757D] mr-2">URL</span><a href={suggested.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline break-all">{suggested.url}</a></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-[#6C757D] mr-2">Description</span><span className="text-sm whitespace-pre-wrap">{suggested.description}</span></div>
                </div>
              </div>
            );
          })}
          {visible.length === 0 && (
            <div className="py-8 text-center text-sm text-[#6C757D]">
              {all.length === 0 ? "No suggestions — the AI didn't find anything to refine." : "No suggestions left to review."}
            </div>
          )}
        </div>

        <DialogFooter>
          <SecondaryButton onClick={close} data-testid="refine-entries-close">Close</SecondaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDiscard({ onApprove, onDiscard, testidPrefix }) {
  return (
    <div className="flex gap-2 shrink-0">
      <button type="button" onClick={onApprove} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-medium bg-leaf text-white border-leaf hover:bg-leaf/90" data-testid={`${testidPrefix}-approve`}>
        <Check className="w-3.5 h-3.5" /> Approve
      </button>
      <button type="button" onClick={onDiscard} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs font-medium bg-white text-clay border-clay hover:bg-clay/5" data-testid={`${testidPrefix}-discard`}>
        <X className="w-3.5 h-3.5" /> Discard
      </button>
    </div>
  );
}

function FieldRow({ label, current, suggested, changed }) {
  return (
    <div className="mb-2">
      <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">{label}{!changed && <span className="ml-2 normal-case text-[#A0A6AB]">(unchanged)</span>}</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-sm whitespace-pre-wrap">{current || <span className="italic text-[#6C757D]">empty</span>}</div>
        <div className={`text-sm whitespace-pre-wrap ${changed ? "font-medium" : "text-[#6C757D]"}`}>{suggested || <span className="italic text-[#6C757D]">empty</span>}</div>
      </div>
    </div>
  );
}
